import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`} className="text-xl text-[#D4AF37] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h2>
    <div className="text-white/60 text-sm leading-relaxed font-light space-y-3">{children}</div>
  </section>
);

function RefundHardcoded() {
  return (
    <div data-testid="refund-page" className="min-h-screen">
      <SEOHead
        title="Refund & Return Policy"
        description="JS Fragrance Refund Policy — eligibility window, evidence requirements, refund methods, exchanges, and how to start a claim. 7-day return window for damaged or wrong items."
        url="https://jsfragrances.com/refund"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Refund Policy', path: '/refund' }]} />

      <div className="bg-[#0A0A0A] py-12 sm:py-16 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Legal</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Refund & Return Policy
          </h1>
          <p className="text-white/40 text-sm mt-3 font-sans">Last updated: February 1, 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-10">
        <Section id="our-promise" title="Our Promise">
          <p>
            Every JS Fragrance bottle is hand-blended in Manila as <strong className="text-white/80">Eau de Parfum at 30% oil concentration
            (Extrait-level strength)</strong> and goes through quality control before shipping. If your order arrives damaged, defective, or
            incorrect, we&apos;ll make it right — quickly and at no extra cost to you.
          </p>
        </Section>

        <Section id="tester-kit-refund" title="Tester Kit — Non-Refundable">
          <div className="bg-red-500/5 border border-red-500/20 p-4 text-white/80 mb-3">
            <p>
              <strong>The Brand Ambassador Tester Kit (currently ₱1,899) is strictly non-refundable</strong> once your payment has been
              approved by our team. The kit is custom-prepared per ambassador and contains finished hand-blended product. Please
              plan accordingly before submitting your payment.
            </p>
          </div>
          <p>
            If your tester-kit payment is <strong className="text-white/80">REJECTED</strong> (mismatched amount, unclear screenshot,
            wrong reference, etc.), no charge stands and you are free to resubmit a corrected proof. If your payment is
            <strong className="text-white/80"> APPROVED</strong>, the purchase is final.
          </p>
          <p>
            If your ambassador status is later terminated for cause (e.g., reselling counterfeits, deceptive marketing), the
            tester-kit fee is forfeited along with any wallet balance.
          </p>
        </Section>

        <Section id="ambassador-wallet" title="Ambassador Wallet (₱200 Referral Bonus)">
          <p>
            The one-time ₱200 referral bonus credited when your DIRECT referee&apos;s tester-kit payment is approved is recorded
            in your ambassador wallet. Wallet credit can be applied to your next product order placed via JS, or cashed out
            per the cashout schedule communicated to active ambassadors. Wallet balance is non-transferable and is forfeited
            if your ambassador status is terminated for cause.
          </p>
          <p className="text-white/40">
            We are a one-level program — no downline, no chain commissions. The ₱200 applies only to direct referrals, only on
            the referee&apos;s tester-kit approval, never on their subsequent product purchases.
          </p>
        </Section>

        <Section id="prize-claim" title="Monthly Top-Seller Prize Claims">
          <p>
            The Monthly Top-Seller bundle (cash bonus via GCash, free 50ml bottle of the winner&apos;s choice, spotlight feature post, and
            official downloadable certificate) is <strong className="text-white/80">claimed by the winner</strong> — not auto-shipped.
            Winners file a claim from their certificate page (<span className="font-mono text-white/50">/certificate/&#123;year&#125;/&#123;month&#125;</span>)
            using the &quot;Claim Your Prize&quot; button, providing their GCash number + preferred scent + any special notes.
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Server-side validation confirms the user really was the #1 seller for that month before accepting the claim.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Claims are reviewed and fulfilled by admin within <strong className="text-white/80">1–2 business days</strong>.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Once a claim is marked <strong className="text-white/80">fulfilled</strong> (cash sent + bottle shipped + spotlight posted), it cannot be re-claimed.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> If your free bottle arrives damaged, the standard 48-hour damage-claim window applies (see below).</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Prizes are non-transferable to another account and cannot be converted to cash above the announced cash-bonus amount.</li>
          </ul>
        </Section>

        <Section id="distributor-refund" title="Distributor Bulk Orders (MOQ 100+)">
          <p>
            Bulk distributor orders require full payment in advance. Once production has started, distributor orders are
            <strong className="text-white/80"> non-cancellable</strong>. Returns are limited to:
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Manufacturing defects (e.g., faulty atomizers, leaking bottles)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Shipping damage</li>
          </ul>
          <p>
            All defects/damage must be reported within <strong className="text-white/80">3 days of delivery</strong> with photo
            evidence. Approved returns may be issued as replacement product, store credit, or partial refund — at JS&apos;s
            discretion based on the issue.
          </p>
        </Section>

        <Section id="eligibility" title="Customer Refund & Exchange Eligibility">
          <p>Due to the personal-care nature of fragrance and hygiene considerations, refunds and exchanges are limited to:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Damaged or defective product on arrival</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Wrong item delivered (different scent, size, or variant from what you ordered)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Missing items from a multi-item order</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Item declared lost in transit by the courier</li>
          </ul>
          <p>
            <strong className="text-white/80">Reporting window:</strong> within <strong className="text-white/80">48 hours</strong> of delivery
            for damage/wrong-item claims, and within <strong className="text-white/80">7 days</strong> for hidden defects.
          </p>
        </Section>

        <Section id="not-eligible" title="What's Not Eligible">
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> The Brand Ambassador Tester Kit (non-refundable once approved — see above)</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Bottles that have been opened, sprayed, or used (unless defective)</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Change of mind or scent preference disagreements after delivery</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Custom Scent Builder personalised orders (made-to-order, except for defects)</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Distributor bulk orders that have entered production (non-cancellable)</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Items damaged by misuse, improper storage, or after the buyer received them in good condition</li>
            <li className="flex gap-2"><span className="text-white/30 mt-1.5">◆</span> Failed delivery due to incorrect address provided at checkout (re-shipping fees apply)</li>
          </ul>
        </Section>

        <Section id="how-to-request" title="How to Request a Refund or Exchange">
          <div className="space-y-4">
            {[
              { step: '1', text: 'Within 48 hours of delivery, take clear photos and (ideally) a short video of the damaged item, defect, or wrong product still in the original packaging.' },
              { step: '2', text: 'Email support@jsfragrances.com with your order number, photos/video, and a brief description. Or use the Contact form on /about.' },
              { step: '3', text: 'Our team reviews within 1-2 business days and confirms next steps — replacement, refund, or store credit.' },
              { step: '4', text: 'For exchanges of physical items, we arrange courier pickup or schedule a return drop-off (free for our error; customer-paid for change-of-mind cases that we approve as exceptions).' },
              { step: '5', text: 'Refunds are processed within 5-7 business days to the original payment method. Bank transfer reversals may take an additional 2-3 banking days.' },
            ].map(item => (
              <div key={item.step} className="flex gap-3 items-start">
                <span className="w-6 h-6 flex items-center justify-center bg-[#D4AF37] text-black text-xs font-medium flex-shrink-0">{item.step}</span>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="refund-method" title="Refund Methods">
          <p>Refunds are issued back to the original payment method whenever possible:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> GCash / Maya / GoTyme — refunded directly to your e-wallet</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Bank transfer — refunded to the originating account</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Credit/debit card — refunded via the same processor (5-10 banking days for the bank to post)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Cash on Delivery — refunded via GCash, Maya, or store credit, your choice</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Store credit (optional) — issued instantly, never expires, can be combined with future promos</li>
          </ul>
        </Section>

        <Section id="partial" title="Partial Refunds">
          <p>
            In some cases — for example, if part of a bundle is defective but the rest of the items are fine — we may issue a
            partial refund proportional to the affected item(s). Bundle discounts will be recalculated based on the kept items.
          </p>
        </Section>

        <Section id="lost-shipment" title="Lost or Stolen Shipments">
          <p>
            If your tracking shows &quot;delivered&quot; but the package never arrived, contact us immediately. We&apos;ll open a courier
            investigation. If the courier confirms the loss, we replace or refund. We are not liable for packages stolen from
            unattended doorsteps, unsecured mailboxes, or community drop-points.
          </p>
        </Section>

        <Section id="exchanges" title="Exchanges">
          <p>
            For wrong or damaged items, the replacement ships once we receive and verify the returned item — or sooner, at our
            discretion. Shipping costs for exchanges due to our error are covered by JS Fragrance. We do not exchange for
            change-of-mind reasons after the bottle has been opened.
          </p>
        </Section>

        <Section id="contact" title="Need Help With a Claim?">
          <p>
            We&apos;re responsive and we want to make this painless. Reach out:
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Email: <a href="mailto:support@jsfragrances.com" className="text-[#D4AF37] hover:underline">support@jsfragrances.com</a></li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Contact form: <a href="/about#contact" className="text-[#D4AF37] hover:underline">jsfragrances.com/about#contact</a></li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Facebook: <a href="https://www.facebook.com/profile.php?id=61588041885547" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">JS Fragrance</a></li>
          </ul>
        </Section>
      </div>
    </div>
  );
}

export default function RefundPage() {
  return <RefundHardcoded />;
}
